/**
 * Company Profiles Hook
 * Provides access to multiple company profiles (BV's) with live updates
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CompanyProfile, profileToBranding } from '@/types/branding.types';
import {
  watchCompanyProfiles,
  addCompanyProfile,
  updateCompanyProfile,
  deleteCompanyProfile,
  setDefaultProfile,
  getCompanyProfileById,
} from '@/services/companyProfiles.service';
import { uploadOptimizedLogo, deleteProfileLogos } from '@/lib/storage/logoUploadService';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import i18n from '@/app/i18n/i18n';

// Helper to get translation
const t = (key: string) => i18n.t(`companyProfiles.toast.${key}`, { ns: 'settings' });

export type { CompanyProfile };
export { profileToBranding };

/**
 * Hook to access company profiles with live updates
 */
export function useCompanyProfiles(teamId: string | undefined) {
  const queryClient = useQueryClient();
  const [profiles, setProfiles] = useState<CompanyProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Set up real-time listener
  useEffect(() => {
    if (!teamId) {
      setProfiles([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const unsubscribe = watchCompanyProfiles(teamId, (newProfiles) => {
      setProfiles(newProfiles);
      setIsLoading(false);
      // Update query cache for consistency
      queryClient.setQueryData(['companyProfiles', teamId], newProfiles);
    });

    return () => {
      unsubscribe();
    };
  }, [teamId, queryClient]);

  // Get default profile
  const defaultProfile = profiles.find((p) => p.isDefault) || profiles[0] || null;

  // Add profile mutation
  const addProfileMutation = useMutation({
    mutationFn: async (profile: Omit<CompanyProfile, 'id' | 'createdAt' | 'updatedAt'>) => {
      if (!teamId) throw new Error('No team ID');
      return addCompanyProfile(teamId, profile);
    },
    onSuccess: () => {
      toast.success(t('added'));
    },
    onError: (error) => {
      logger.error('[useCompanyProfiles] Failed to add profile:', error);
      toast.error(t('addFailed'));
    },
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async ({
      profileId,
      updates,
    }: {
      profileId: string;
      updates: Partial<Omit<CompanyProfile, 'id' | 'createdAt'>>;
    }) => {
      if (!teamId) throw new Error('No team ID');
      return updateCompanyProfile(teamId, profileId, updates);
    },
    onSuccess: () => {
      toast.success(t('updated'));
    },
    onError: (error) => {
      logger.error('[useCompanyProfiles] Failed to update profile:', error);
      toast.error(t('updateFailed'));
    },
  });

  // Delete profile mutation
  const deleteProfileMutation = useMutation({
    mutationFn: async (profileId: string) => {
      if (!teamId) throw new Error('No team ID');
      return deleteCompanyProfile(teamId, profileId);
    },
    onSuccess: () => {
      toast.success(t('deleted'));
    },
    onError: (error) => {
      logger.error('[useCompanyProfiles] Failed to delete profile:', error);
      if (error instanceof Error && error.message.includes('last')) {
        toast.error(i18n.t('companyProfiles.delete.cannotDeleteLast', { ns: 'settings' }));
      } else {
        toast.error(t('deleteFailed'));
      }
    },
  });

  // Set default mutation
  const setDefaultMutation = useMutation({
    mutationFn: async (profileId: string) => {
      if (!teamId) throw new Error('No team ID');
      return setDefaultProfile(teamId, profileId);
    },
    onSuccess: () => {
      toast.success(t('defaultSet'));
    },
    onError: (error) => {
      logger.error('[useCompanyProfiles] Failed to set default:', error);
      toast.error(t('defaultFailed'));
    },
  });

  // Upload logo mutation (with optimization)
  const uploadLogoMutation = useMutation({
    mutationFn: async ({ profileId, file }: { profileId: string; file: File }) => {
      if (!teamId) throw new Error('No team ID');

      // Upload optimized logo variants
      const result = await uploadOptimizedLogo(teamId, profileId, file);

      if (!result.success) {
        throw new Error(result.error);
      }

      // Update profile with the invoice URL (primary logo for backward compatibility)
      await updateCompanyProfile(teamId, profileId, {
        logoUrl: result.metadata.invoiceUrl,
      });

      return result.metadata.invoiceUrl;
    },
    onSuccess: () => {
      // Invalidate all company profile queries to ensure fresh data
      // This is critical for PDF generation which may use cached profile data
      void queryClient.invalidateQueries({ queryKey: ['companyProfiles'] });
      void queryClient.invalidateQueries({ queryKey: ['companyProfile'] });
      toast.success(t('logoUploaded'));
    },
    onError: (error) => {
      logger.error('[useCompanyProfiles] Failed to upload logo:', error);
      toast.error(t('logoFailed'));
    },
  });

  // Remove logo mutation
  const removeLogoMutation = useMutation({
    mutationFn: async (profileId: string) => {
      if (!teamId) throw new Error('No team ID');

      // Delete logo files from storage
      await deleteProfileLogos(teamId, profileId);

      // Clear logo URL in profile
      await updateCompanyProfile(teamId, profileId, { logoUrl: undefined });
    },
    onSuccess: () => {
      // Invalidate all company profile queries to ensure fresh data
      void queryClient.invalidateQueries({ queryKey: ['companyProfiles'] });
      void queryClient.invalidateQueries({ queryKey: ['companyProfile'] });
      toast.success(t('logoRemoved'));
    },
    onError: (error) => {
      logger.error('[useCompanyProfiles] Failed to remove logo:', error);
      toast.error(t('logoRemoveFailed'));
    },
  });

  return {
    profiles,
    defaultProfile,
    isLoading,
    addProfile: addProfileMutation.mutate,
    addProfileAsync: addProfileMutation.mutateAsync, // Returns the created profile
    updateProfile: updateProfileMutation.mutate,
    deleteProfile: deleteProfileMutation.mutate,
    setDefault: setDefaultMutation.mutate,
    uploadLogo: uploadLogoMutation.mutate,
    uploadLogoAsync: uploadLogoMutation.mutateAsync, // Returns the logo URL
    removeLogo: removeLogoMutation.mutate,
    removeLogoAsync: removeLogoMutation.mutateAsync,
    isAdding: addProfileMutation.isPending,
    isUpdating: updateProfileMutation.isPending,
    isDeleting: deleteProfileMutation.isPending,
    isRemovingLogo: removeLogoMutation.isPending,
  };
}

/**
 * Hook to get a specific company profile by ID
 * Falls back to default profile if not found
 */
export function useCompanyProfile(
  teamId: string | undefined,
  profileId: string | undefined | null
) {
  return useQuery({
    queryKey: ['companyProfile', teamId, profileId],
    queryFn: async () => {
      if (!teamId) return null;
      return getCompanyProfileById(teamId, profileId);
    },
    enabled: !!teamId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to get the default company profile
 */
export function useDefaultCompanyProfile(teamId: string | undefined) {
  const { profiles, defaultProfile, isLoading } = useCompanyProfiles(teamId);
  return { profile: defaultProfile, profiles, isLoading };
}
